import { useCallback, useEffect } from "react";
import { apiFetch } from "@/lib/api/client";
import { buildClients, buildInstances } from "@/lib/mappers";
import type { Client, CronJob, CronRun, GatewayAccount, Instance, MiniInstallation, MiniSourceInfo, ServerTarget } from "@/types";

type Setter<T> = (value: T | ((previous: T) => T)) => void;

export interface AppDataState {
  setLoading: Setter<boolean>;
  setApiOnline: Setter<boolean>;
  setMiniSource: Setter<MiniSourceInfo | null>;
  setInstallations: Setter<MiniInstallation[]>;
  setClients: Setter<Client[]>;
  setInstances: Setter<Instance[]>;
  setSelectedClientId: Setter<string>;
  setServers: Setter<ServerTarget[]>;
  setCronJobs: Setter<CronJob[]>;
  setCronRuns: Setter<CronRun[]>;
  setGatewayAccounts: Setter<GatewayAccount[]>;
  setProxyUrl: Setter<string>;
}

export function useAppData(authenticated: boolean, state: AppDataState, refreshInFlight: { current: boolean }) {
  const { setLoading, setApiOnline, setMiniSource, setInstallations, setClients, setInstances, setSelectedClientId, setServers, setCronJobs, setCronRuns, setGatewayAccounts, setProxyUrl } = state;
  const refreshData = useCallback(async (options: { force?: boolean } = {}) => {
    if (refreshInFlight.current && !options.force) return;
    refreshInFlight.current = true;
    try {
      const health = await (await apiFetch("/api/health")).json();
      setApiOnline(Boolean(health.ok));
      setMiniSource(health.miniSource || null);
      const instancesPayload = await (await apiFetch("/api/instances")).json();
      const installations = Array.isArray(instancesPayload.instances) ? instancesPayload.instances : [];
      setInstallations(installations);
      const clientsPayload = await (await apiFetch("/api/clients")).json();
      const licensesPayload = await (await apiFetch("/api/licenses")).json();
      const clients = buildClients(clientsPayload.clients || [], licensesPayload.licenses || [], installations);
      setClients(clients);
      setInstances(buildInstances(installations));
      setSelectedClientId((current) => current && clients.some((item) => item.id === current) ? current : clients[0]?.id || "");
      const servers = await (await apiFetch("/api/servers")).json();
      setServers(Array.isArray(servers.servers) ? servers.servers : []);
      const cron = await (await apiFetch("/api/cron/jobs")).json();
      const jobs = Array.isArray(cron.jobs) ? cron.jobs : [];
      setCronJobs(jobs);
      if (jobs[0]?.id) {
        const runs = await (await apiFetch(`/api/cron/jobs/${encodeURIComponent(jobs[0].id)}/runs`)).json();
        setCronRuns(Array.isArray(runs.runs) ? runs.runs : []);
      } else setCronRuns([]);
      try {
        const gateway = await (await apiFetch("/api/gateway/accounts")).json();
        setGatewayAccounts(Array.isArray(gateway.accounts) ? gateway.accounts : []);
      } catch { setGatewayAccounts([]); }
      try {
        const response = await apiFetch("/api/settings");
        const settings = await response.json();
        if (response.ok && settings.ok !== false) setProxyUrl(settings.settings?.aiProxyUrl || "");
      } catch { /* optional endpoint */ }
    } catch {
      setApiOnline(false); setInstallations([]); setClients([]); setInstances([]); setServers([]); setCronJobs([]); setCronRuns([]); setGatewayAccounts([]); setMiniSource(null);
    } finally {
      refreshInFlight.current = false;
      setLoading(false);
    }
  }, [refreshInFlight, setApiOnline, setClients, setCronJobs, setCronRuns, setGatewayAccounts, setInstallations, setInstances, setLoading, setMiniSource, setProxyUrl, setSelectedClientId, setServers]);

  useEffect(() => {
    if (!authenticated) return;
    void refreshData();
    const timer = window.setInterval(refreshData, 5000);
    const onVisibilityChange = () => document.visibilityState === "visible" && void refreshData();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", onVisibilityChange); };
  }, [authenticated, refreshData]);

  return refreshData;
}
